import { Suspense, lazy } from "react";
import type { CostSummaryDay } from "../api";
import styles from "./SpendBars.module.css";
import type { SpendStack } from "./SpendChartEcharts";

// echarts is heavy (~1MB), so the chart is split into its own async chunk and
// only fetched when a SpendBars actually renders with data - it never lands in
// the initial bundle (it loads when the operator opens the AI Costs page).
const SpendChartEcharts = lazy(() => import("./SpendChartEcharts"));

interface Props {
  // Per-day rollup from /cost/summary. Each bar is one day. An all-zero window
  // still renders (a flat axis) rather than the empty state.
  days: CostSummaryDay[];
  // Empty-state copy shown when there's no spend data in the window at all.
  emptyLabel?: string;
  // Chart height in px. Defaults to the compact size the old CSS bars used,
  // plus room for the value axis the chart now draws.
  height?: number;
  // Stacked groups to draw in place of each day's total, one value per day each.
  stacks?: SpendStack[];
}

// Per-day spend bar chart (ECharts). Replaced the hand-rolled CSS bar series so
// each day gets a real hover tooltip (date + $ spend + run count) for free.
// Kept as a named component (stable public API: days/emptyLabel/height) so the
// AI Costs page calls it the same way regardless of the chart implementation.
export const SpendBars = ({
  days,
  emptyLabel = "No priced runs in this window yet.",
  height = 150,
  stacks,
}: Props) => {
  if (days.length === 0) {
    return <div className={styles.empty}>{emptyLabel}</div>;
  }

  return (
    <Suspense
      fallback={
        <div className={styles.loading} style={{ height }}>
          Loading chart...
        </div>
      }
    >
      <SpendChartEcharts days={days} height={height} stacks={stacks} />
    </Suspense>
  );
};

export default SpendBars;
